/**
 * The rules, in the player's words.
 *
 * Every noun on the home screen — pot, bank, push, rung and ladder — was jargon
 * defined nowhere in the app, which made an otherwise legible screen unreadable
 * to anybody who had not already played. This is the definition, and it is
 * INLINE rather than a route or a modal: no navigation to lose your place in,
 * no dismissal state to persist, and it costs a first-time visitor one tap.
 *
 * The one-line summary above the toggle is always visible, because the single
 * most important sentence in the game should not be behind a disclosure.
 *
 * "RUNG" IS GONE FROM THE COPY, ladder and all. It is the right word inside the
 * rules package and the wrong one on a screen: playtesters read "push · rung 2"
 * as an offer of a second go at the globe, and took two or three matches to
 * work out that it buys a QUESTION. Rung 1 is the pin, everything above it is a
 * numbered question, and `lib/format` owns the arithmetic. Golden rung keeps
 * its name — it is a proper noun with its own flame and nobody has ever been
 * confused by it.
 */

import { useState } from 'react';

interface Step {
  readonly n: string;
  readonly title: string;
  readonly body: string;
}

const STEPS: readonly Step[] = [
  {
    n: '1',
    title: 'Pin it',
    body: 'You get a prompt: a country, or a city. Tap the globe where you think it is. The closer you land, the bigger the pot you win.',
  },
  {
    n: '2',
    title: 'Bank it, or push',
    body: 'Bank keeps the pot for good and ends the round. Push bets the whole pot on a trivia question about the same place. Get it right and the pot multiplies. Get it wrong and you lose all of it. You are never asked to find it on the globe twice.',
  },
  {
    n: '3',
    title: 'Keep climbing, or stop',
    body: 'Clear a question and you get the same choice again, for more. Each one pays better than the last, and you decide how far to go. Your opponent is doing this at the same time.',
  },
  {
    n: '4',
    title: 'Highest total wins',
    body: 'Five rounds, banked points only. Anything still in the pot when you miss is gone. One round per match is golden: its last question pays triple.',
  },
];

/**
 * WHAT THE QUESTIONS ARE ABOUT.
 *
 * Public information, and the point of showing it is that "bet it on a trivia
 * question" is a bet nobody can price without knowing the subject. It is the
 * category list, never the ladder order — which probe lands on which step is
 * drawn per round from a difficulty band (`bandFor` in the rules package), so a
 * fixed running order printed here would be a lie about half the time.
 *
 * It also happens to be the part of the game playtesters liked most, which is
 * an argument for showing it rather than hiding it.
 */
const TOPICS: readonly string[] = [
  'true or false',
  'the capital',
  'population',
  'the flag',
  'land borders',
  'languages, in order',
  'distances',
];

export function HowToPlay(): React.JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <div className="gg-howto">
      <p className="gg-howto-line">
        Pin the target on the globe to win a pot. Then <strong>bank</strong> it to keep it, or{' '}
        <strong>push</strong> for a trivia question that multiplies it and lose the lot if you miss.
      </p>

      <button
        type="button"
        className="gg-howto-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? 'Hide the rules' : 'How to play'}
        <span className="gg-howto-caret" data-open={open ? 'true' : 'false'} aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        <>
          <ol className="gg-howto-steps">
            {STEPS.map((s) => (
              <li key={s.n} className="gg-howto-step">
                <span className="gg-howto-num" aria-hidden="true">
                  {s.n}
                </span>
                <div>
                  <p className="gg-howto-step-title">{s.title}</p>
                  <p className="gg-howto-step-body">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="gg-howto-topics">
            <p className="gg-howto-topics-title">The questions come from</p>
            <ul className="gg-topic-row">
              {TOPICS.map((t) => (
                <li key={t} className="gg-topic">
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}
